<script lang="ts">
  import Icon from './Icon.svelte';
  import { writable } from 'svelte/store';
  import { router } from '@inertiajs/svelte';
  import {
    projectTooltipState,
    cancelHideProjectTooltip,
    hideProjectTooltip,
  } from '../lib/stores/projectTooltip';
  import { openProjectModalForEdit, openProjectRail } from '../lib/stores/ui';
  import { categoryIcons } from '../lib/utils';
  import { getTaskCounts, subscribeToProjectTaskCounts } from '../lib/useProjectTaskCounts';
  import type { TaskCounts } from '../lib/stores/projectTasksCache';
  import { coarsePointer } from '../lib/stores/viewport';

  const STATUS_CONFIG: Record<string, { label: string; color: string; dotColor: string }> = {
    planned: { label: 'Planned', color: '#60a5fa', dotColor: '#3b82f6' },
    active: { label: 'Active', color: '#f59e0b', dotColor: '#d97706' },
    almost: { label: 'Almost', color: '#fb923c', dotColor: '#ea580c' },
    done: { label: 'Done', color: '#4ade80', dotColor: '#16a34a' },
  };

  const CATEGORY_LABELS: Record<string, string> = {
    tech: 'Tech',
    health: 'Health',
    personal: 'Personal',
    finance: 'Finance',
    travel: 'Travel',
    other: 'Other',
  };

  const CATEGORY_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
    tech: { bg: '#dbeafe', text: '#1e40af' },
    health: { bg: '#dcfce7', text: '#166534' },
    personal: { bg: '#fce7f3', text: '#9d174d' },
    finance: { bg: '#fef9c3', text: '#854d0e' },
    travel: { bg: '#e0e7ff', text: '#3730a3' },
    other: { bg: '#f3f4f6', text: '#374151' },
  };

  // Viewport tracking for edge-aware positioning
  const viewportW = writable(window.innerWidth);
  const viewportH = writable(window.innerHeight);
  function handleResize() {
    viewportW.set(window.innerWidth);
    viewportH.set(window.innerHeight);
  }

  const PRIORITY_BORDER_COLORS: Record<string, string> = {
    high: '#ef4444',
    medium: '#fb923c',
    low: '#60a5fa',
    none: '#d1d5db',
  };

  let tooltipState = $derived($projectTooltipState);

  // Reactive taskCounts - updates when tasks are modified in ProjectRail
  let reactiveTaskCounts: TaskCounts | null = $state(null);

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: projectTasksCache pub/sub, re-subscribes when tooltip project changes, returns cleanup
  $effect(() => {
    if (!tooltipState?.project) {
      reactiveTaskCounts = null;
      return;
    }
    // Initialize with current counts
    reactiveTaskCounts = getTaskCounts(tooltipState.project);
    // Subscribe to updates
    return subscribeToProjectTaskCounts(tooltipState.project, (counts) => {
      reactiveTaskCounts = counts;
    });
  });

  let style = $derived.by(() => {
    if (!tooltipState) return 'display: none;';
    const { anchorRect } = tooltipState;
    const popW = 280;
    const popH = 240;
    const gap = 6;
    const vw = $viewportW;
    const vh = $viewportH;

    // Position at bottom-right corner of the chip, offset down so it
    // doesn't block horizontal mouse scanning across a row of chips.
    let left = anchorRect.right - popW;
    let top = anchorRect.bottom + gap;

    // If it overflows right, align to right edge of chip
    if (left + popW > vw - 8) {
      left = vw - popW - 8;
    }
    // If it overflows left, push right
    if (left < 8) {
      left = 8;
    }

    // If it overflows bottom, flip above the chip
    if (top + popH > vh - 8) {
      top = anchorRect.top - popH - gap;
      // If that also overflows top, just pin to bottom of viewport
      if (top < 8) {
        top = vh - popH - 8;
      }
    }

    return `left: ${left}px; top: ${top}px; width: ${popW}px; max-height: ${vh - 16}px;`;
  });
</script>

<svelte:window onresize={handleResize} />

<!-- Hover-only: never on touch, where it would open on a tap and get stuck. -->
{#if tooltipState && !$coarsePointer}
  {@const project = tooltipState.project}
  {@const statusCfg = STATUS_CONFIG[project.status]}
  {@const badgeColors = CATEGORY_BADGE_COLORS[project.category]}
  {@const counts = reactiveTaskCounts || { total: 0, done: 0, todo: 0 }}
  {@const hasTaskProgress = counts.total > 0}
  {@const taskPct = hasTaskProgress ? Math.round((counts.done / counts.total) * 100) : 0}
  {@const incompleteTasks = project.incompleteTasks || []}
  {@const totalIncomplete = counts.todo}
  {@const shownTasks = incompleteTasks.slice(0, 5)}
  {@const remainingCount = totalIncomplete - shownTasks.length}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="project-tooltip"
    {style}
    onmouseenter={cancelHideProjectTooltip}
    onmouseleave={() => hideProjectTooltip()}
  >
    <!-- Header: title + actions top-right -->
    <div class="tooltip-header">
      <div class="header-left">
        <span class="tooltip-title">{project.title}</span>
        {#if project.isPriority}
          <span class="tooltip-star">★</span>
        {/if}
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="action-btn"
          title="Edit"
          onclick={() => {
            const p = project;
            hideProjectTooltip(true);
            openProjectModalForEdit(p);
          }}><Icon name="edit" size={12} label="Edit" /></button
        >
        <button
          type="button"
          class="action-btn"
          title="Full page"
          onclick={() => {
            const id = project.id;
            hideProjectTooltip(true);
            router.visit(`/projects/${id}`);
          }}><Icon name="open" size={12} label="Full page" /></button
        >
      </div>
    </div>

    <!-- Meta row: status + category badge + tags -->
    <div class="tooltip-meta">
      <span class="tooltip-status">
        <span class="status-dot" style="background: {statusCfg.dotColor};"></span>
        {statusCfg.label}
      </span>
      <span class="category-badge" style="background: {badgeColors.bg}; color: {badgeColors.text};">
        {categoryIcons[project.category]}
        {CATEGORY_LABELS[project.category]}
      </span>
      {#if project.multiWeek && project.weekIndex && project.totalWeeks}
        <span class="tooltip-tag">Week {project.weekIndex}/{project.totalWeeks}</span>
      {/if}
    </div>

    <!-- Task progress bar -->
    {#if hasTaskProgress}
      <div class="tooltip-tasks">
        <div class="task-bar-track">
          <div
            class="task-bar-fill"
            style="width: {taskPct}%; background: {statusCfg.color};"
          ></div>
        </div>
        <span class="task-label">{counts.done}/{counts.total} tasks</span>
      </div>
    {/if}

    <!-- Incomplete tasks preview -->
    {#if shownTasks.length > 0}
      <div class="tooltip-task-list">
        {#each shownTasks as task (task.id)}
          <div class="task-item">
            <span
              class="task-checkbox-preview"
              style="border-color: {PRIORITY_BORDER_COLORS[task.priority] || '#d1d5db'};"
            >
              <span class="checkbox-inner"></span>
            </span>
            <span class="task-item-title">{task.title}</span>
          </div>
        {/each}
        {#if remainingCount > 0}
          <button
            type="button"
            class="task-list-link"
            onclick={() => {
              const id = project.id;
              hideProjectTooltip(true);
              openProjectRail(id);
            }}>+{remainingCount} more tasks</button
          >
        {:else}
          <button
            type="button"
            class="task-list-link"
            onclick={() => {
              const id = project.id;
              hideProjectTooltip(true);
              openProjectRail(id);
            }}>Add more tasks</button
          >
        {/if}
      </div>
    {:else}
      <div class="tooltip-task-list">
        <button
          type="button"
          class="task-list-link"
          onclick={() => {
            const id = project.id;
            hideProjectTooltip(true);
            openProjectRail(id);
          }}>Add tasks</button
        >
      </div>
    {/if}

    <!-- Notes preview -->
    {#if project.notes}
      <div class="tooltip-notes">{project.notes}</div>
    {/if}
  </div>
{/if}

<style>
  .project-tooltip {
    position: fixed;
    z-index: 200;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow:
      0 8px 20px rgba(0, 0, 0, 0.12),
      0 2px 6px rgba(0, 0, 0, 0.06);
    padding: 0.625rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    pointer-events: auto;
  }

  .tooltip-header {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    justify-content: space-between;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    flex: 1;
  }

  .tooltip-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.3;
    word-break: break-word;
    min-width: 0;
  }

  .tooltip-star {
    color: #facc15;
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    flex-shrink: 0;
    margin-left: 0.25rem;
  }

  .action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.375rem;
    height: 1.375rem;
    font-size: 0.6875rem;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.12s;
    padding: 0;
    line-height: 1;
    color: #9ca3af;
  }

  .action-btn:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #3b82f6;
  }

  .tooltip-meta {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.6875rem;
    color: #6b7280;
    flex-wrap: wrap;
  }

  .tooltip-status {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-weight: 500;
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .category-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    font-size: 0.625rem;
    font-weight: 600;
    padding: 0.0625rem 0.375rem;
    border-radius: 9999px;
    white-space: nowrap;
  }

  .tooltip-tag {
    color: #6b7280;
    font-size: 0.625rem;
    background: #f3f4f6;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.1875rem;
  }

  .tooltip-tasks {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .task-bar-track {
    flex: 1;
    height: 4px;
    background: #e5e7eb;
    border-radius: 2px;
    overflow: hidden;
  }

  .task-bar-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.3s;
  }

  .task-label {
    font-size: 0.625rem;
    color: #6b7280;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .tooltip-task-list {
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
    border-top: 1px solid #f3f4f6;
    padding-top: 0.375rem;
  }

  .task-item {
    display: flex;
    align-items: center;
    gap: 0.3125rem;
    padding: 0.0625rem 0;
    min-width: 0;
  }

  .task-checkbox-preview {
    width: 0.8125rem;
    height: 0.8125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid #d1d5db;
    border-radius: 0.1875rem;
    background: white;
    flex-shrink: 0;
  }

  .checkbox-inner {
    width: 0;
    height: 0;
  }

  .task-item-title {
    font-size: 0.6875rem;
    color: #374151;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1;
  }

  .task-list-link {
    font-size: 0.625rem;
    font-weight: 500;
    color: #2563eb;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.125rem 0;
    padding-left: 1.125rem;
    text-align: left;
    transition: color 0.12s;
  }

  .task-list-link:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  .tooltip-notes {
    font-size: 0.6875rem;
    color: #4b5563;
    line-height: 1.4;
    border-top: 1px solid #f3f4f6;
    padding-top: 0.375rem;
    white-space: pre-wrap;
    max-height: 3rem;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
  }
</style>
